import { useCallback, useContext, useEffect, useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import "./feedbackResult.css";
import { Button, Pagination, Table, TableCell, TableHead, TableRow } from "@mui/material";
import { getChangeLogImport, getComponentsImport } from "../gametable/projectImports";
import { UserContext } from "../userContext";

function FeedbackResultRow(props) {
  let row = [];
  let data = props.data;
  let versionInfo = props.versionInfo;
  let headerList = props.headerList;
  const [noteSaved, setNoteSaved] = useState(false);
  const saveNote = useCallback(
    (evt) => {
      fetch("/api/submit-feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          answers: { note: evt.target.innerText },
          user: props.data.username,
          tableId: props.data.tableId,
        }),
      })
        .then((res) => {
          if (res.status === 200) {
            setNoteSaved(true);
            return;
          }
          return Promise.reject(res.text());
        })
        .catch((error) => {
          console.error(error);
        });
      console.log("saving note");
    },
    [props.data],
  );
  const getVersionInfo = useCallback(
    (targetVersion) => {
      let result = "";
      let correctVersions = [];
      for (let versionName in versionInfo) {
        if (versionName.startsWith("__")) {
          versionName = versionName.slice(2);
        }
        if (targetVersion?.startsWith(versionName)) {
          correctVersions.push(versionName);
        }
      }
      correctVersions.sort().reverse();
      for (let v of correctVersions) {
        result += v + ": " + (versionInfo[v] ?? versionInfo["__" + v]);
      }
      return result;
    },
    [versionInfo],
  );
  for (let colId of headerList) {
    if (typeof data.tableId === "object") {
      continue;
    }
    let cellContent = data[colId];
    switch (colId) {
      case "username":
        cellContent = (
          <>
            {<div onClick={() => props.setFilterUser(data[colId])}>{data[colId]}</div>} at{" "}
            <NavLink to={"./../table/" + data.tableId}>{data.tableId}</NavLink>
          </>
        );
        break;
      case "createdTime":
        cellContent = <>{new Date(+data.createdTime).toLocaleString()}</>;
        break;
      case "tableId":
        continue;
      case "version":
        cellContent = (
          <div className="version-name" title={getVersionInfo(data[colId])}>
            {data[colId]}
          </div>
        );
        break;
      case "note":
        cellContent = (
          <div
            className={"feedback-note " + (noteSaved ? "saved" : "")}
            contentEditable
            onKeyDown={() => setNoteSaved(false)}
            onBlur={saveNote}
          >
            {data[colId]}
          </div>
        );
        break;
    }
    row[headerList.indexOf(colId)] = (
      <td key={colId}>
        <div>{cellContent}</div>
      </td>
    );
  }
  return <TableRow key={data.tableId + "-" + data.username}>{row}</TableRow>;
}

function FeedbackResult(props) {
  let params = useParams();
  let projectSlug = params.projectSlug;
  const [feedbackResult, setFeedbackResult] = useState([]);
  const [headerList, setHeaderList] = useState([]);
  const [versionInfo, setVersionInfo] = useState({});
  const [page, setPage] = useState(1);
  const [filterUser, setFilterUser] = useState();

  const userContext = useContext(UserContext);

  useEffect(() => {
    getChangeLogImport(params.projectSlug).then((data) => {
      setVersionInfo(data.default);
    });
    document.title = `Feedback - ${params.projectSlug}`;
  }, [params.projectSlug]);

  function downloadFile(blob, filename) {
    let file = window.URL.createObjectURL(blob);
    let a = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    a.href = file;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(file);
  }

  function downloadStats(evt) {
    evt.preventDefault();
    let queryString = "";
    if (evt.target.minVersion.value !== "") {
      queryString += "minVersion=" + evt.target.minVersion.value + "&";
    }
    if (evt.target.maxVersion.value !== "") {
      queryString += "maxVersion=" + evt.target.maxVersion.value + "&";
    }
    if (evt.target.tablesLimit.value !== "") {
      queryString += "tableLimit=" + evt.target.tablesLimit.value + "&";
    }
    fetch("/api/projects/" + projectSlug + "/get-stats?" + queryString, {
      method: "GET",
    })
      .then((res) => res.blob())
      .then((blob) => {
        downloadFile(blob, projectSlug + "-stats");
      });
    fetch("/api/projects/" + projectSlug + "/get-table-stats-info?" + queryString, {
      method: "GET",
    })
      .then((res) => res.blob())
      .then((blob) => {
        downloadFile(blob, projectSlug + "-tableinfo");
      });
    fetch("/api/projects/" + projectSlug + "/get-object-stats?" + queryString, {
      method: "GET",
    })
      .then((res) => res.blob())
      .then((blob) => {
        downloadFile(blob, projectSlug + "-objectStats");
      });
  }

  useEffect(() => {
    let components = getComponentsImport(params.projectSlug).then((components) => {
      const questionOrder = components.default.__feedbackQuestionOrder ?? [];
      const statsToFeedback = components.default.__statsInFeedback?.statList ?? [];
      const optionsToFeedback = components.default.__optionsInFeedback?.optionList ?? [];
      const globalStatsToFeedback = components.default.__statsInFeedback?.globalStats ?? [];
      setHeaderList([
        "username",
        "color",
        "createdTime",
        "version",
        "note",
        ...optionsToFeedback,
        ...globalStatsToFeedback,
        ...statsToFeedback,
        ...questionOrder,
      ]);
      fetch(
        "/api/get-feedback-result/" +
          params.projectSlug +
          "?" +
          [
            statsToFeedback.map((s) => "stat[]=" + s),
            optionsToFeedback.map((s) => "tableOption[]=" + s),
            globalStatsToFeedback.map((s) => "globalStats[]=" + s),
            ["page=" + page, ...(filterUser ? ["filterUser=" + filterUser] : [])],
          ]
            .flat()
            .join("&"),
        {
          method: "GET",
        },
      )
        .then((response) => {
          console.log(response);
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .then((data) => {
          setFeedbackResult(data);
        });
      console.log(components);
    });
  }, [params.projectSlug, page, filterUser]);

  let changePage = useCallback(
    (evt, value) => {
      setPage(value);
    },
    [setPage],
  );

  let rowData = feedbackResult;

  for (let row of rowData ?? []) {
    Object.assign(row, row.feedbackAnswers ?? {});
    delete row.feedbackAnswers;
    Object.assign(row, row.statValues ?? {});
    delete row.statValues;
    Object.assign(row, row.options ?? {});
    delete row.options;
    for (let colId in row) {
      if (!headerList.includes(colId)) {
        headerList.push(colId);
      }
    }
  }
  //headerList = [...headerList.slice(0, 3), ...headerList.slice(3).sort()];
  let headers = [];
  for (let header of headerList) {
    if (header !== "tableId") {
      headers.push(<TableCell>{header}</TableCell>);
    }
  }

  let paginationControls = <Pagination count={Infinity} page={page} onChange={changePage} />;
  let rows = [];
  for (let data of rowData) {
    console.log("pushing new row", data);
    rows.push(
      <FeedbackResultRow data={data} headerList={headerList} versionInfo={versionInfo} setFilterUser={setFilterUser} />,
    );
  }

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 16 }}>
        <h2>Feedback results for project {params.projectSlug}</h2>
        {userContext.hasPermission("see-ingame-feedback-results") && (
          <Link to={"./../ingame-feedback/"}>Ingame Feedback</Link>
        )}
      </div>
      <note>
        Click a username to filter by that username.{" "}
        {filterUser ? (
          <>
            <span>{`Filtered only feedback by ${filterUser}`}</span>
            <Button onClick={() => setFilterUser()}>Clear filter</Button>
          </>
        ) : null}{" "}
      </note>
      {paginationControls}
      <Table border="1" className="feedback-results">
        <TableHead>
          <TableRow className="feedback-result-header">{headers}</TableRow>
        </TableHead>
        {rows}
      </Table>
      {paginationControls}
    </div>
  );
}

export { FeedbackResult };
